import React, { useEffect, useState } from "react";
import { FaTimes, FaSpinner } from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import { toast } from "react-toastify";

const toDatetimeLocalValue = (date) => {
  const d = date ? new Date(date) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const resolveSubject = (subject, customer) => {
  if (subject?.id) {
    return subject;
  }
  if (customer) {
    return {
      id: customer.userId || customer.driverId,
      name: customer.customerName || customer.driverName || "N/A",
      mobile: customer.mobileNumber || "N/A",
    };
  }
  return null;
};

const NumberSharingNoteModal = ({ show, subject, customer, notesUrl, onClose, onSaved }) => {
  const [sharedAt, setSharedAt] = useState("");
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(false);

  const resolvedSubject = resolveSubject(subject, customer);

  useEffect(() => {
    if (show) {
      setSharedAt(toDatetimeLocalValue(new Date()));
      setComments("");
    }
  }, [show]);

  const handleClose = () => {
    setSharedAt("");
    setComments("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!sharedAt) {
      toast.error("Please select date and time");
      return;
    }

    if (!notesUrl || !resolvedSubject?.id) {
      toast.error("Unable to save note");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(notesUrl, {
        sharedAt: new Date(sharedAt).toISOString(),
        comments: comments.trim(),
      });

      if (response.data?.success) {
        toast.success("Note added");
        onSaved(resolvedSubject.id, response.data.data.numberSharingNotes);
        handleClose();
      } else {
        toast.error(response.data?.message || "Failed to add note");
      }
    } catch (error) {
      console.error("Add number sharing note error:", error);
      toast.error(error.response?.data?.message || "Failed to add note");
    } finally {
      setLoading(false);
    }
  };

  if (!show || !resolvedSubject) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Add note</h3>
          <button type="button" onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <FaTimes />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            {resolvedSubject.name} · {resolvedSubject.mobile}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date and time <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={sharedAt}
              onChange={(e) => setSharedAt(e.target.value)}
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comments
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              disabled={loading}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter comments"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 inline-flex items-center"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NumberSharingNoteModal;
